import React, { useState, useEffect } from 'react';
import { LineChart, Line, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { dashboardAPI } from '../services/api';

const RISK_CONFIG = {
  Low:      { color: '#10b981', bg: 'bg-emerald-500/15', border: 'border-emerald-500/40', text: 'text-emerald-400', emoji: '✅' },
  Mild:     { color: '#3b82f6', bg: 'bg-blue-500/15',    border: 'border-blue-500/40',    text: 'text-blue-400',    emoji: '🔵' },
  Moderate: { color: '#f59e0b', bg: 'bg-yellow-500/15',  border: 'border-yellow-500/40',  text: 'text-yellow-400',  emoji: '⚠️' },
  High:     { color: '#f97316', bg: 'bg-orange-500/15',  border: 'border-orange-500/40',  text: 'text-orange-400',  emoji: '🔴' },
  Critical: { color: '#ef4444', bg: 'bg-red-500/15',     border: 'border-red-500/40',     text: 'text-red-400',     emoji: '🚨' },
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-600 rounded-xl p-3 shadow-2xl">
      {payload.map((p, i) => <p key={i} className="text-sm font-bold" style={{ color: p.color }}>{p.name}: {p.value?.toFixed(1)}</p>)}
    </div>
  );
};

function SummaryCard({ icon, label, value, unit, color, delay = 0 }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, [delay]);
  return (
    <div className={`rounded-2xl p-5 border transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      style={{ background: `${color}12`, borderColor: `${color}35` }}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{icon}</span>
        <span className="text-xs text-slate-400 uppercase tracking-widest font-semibold">{label}</span>
      </div>
      <div className="text-3xl font-black text-white">{value}<span className="text-sm font-normal text-slate-400 ml-1">{unit}</span></div>
    </div>
  );
}

export default function History() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    try {
      const res = await dashboardAPI.getHistory();
      setHistory(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="text-center">
        <div className="relative w-24 h-24 mx-auto mb-6">
          {[0,1,2].map(i => <div key={i} className="absolute inset-0 rounded-full border-2 border-cyan-500/40 animate-ping" style={{ animationDelay: `${i*0.2}s` }}/>)}
          <div className="absolute inset-0 flex items-center justify-center text-4xl">📋</div>
        </div>
        <p className="text-slate-400 text-lg font-medium">Loading History...</p>
      </div>
    </div>
  );

  const filtered = history
    .filter(r => filter === 'All' || r.risk_category === filter)
    .sort((a, b) => sortDir === 'desc'
      ? new Date(b.date) - new Date(a.date)
      : new Date(a.date) - new Date(b.date));

  const trendData = [...history].reverse().map(r => ({ date: r.date, stress: r.stress_score, sleep: r.sleep_hours }));

  const avgStress = history.length ? (history.reduce((s, r) => s + r.stress_score, 0) / history.length).toFixed(1) : 0;
  const avgSleep  = history.length ? (history.reduce((s, r) => s + r.sleep_hours,  0) / history.length).toFixed(1) : 0;
  const avgHR     = history.length ? (history.reduce((s, r) => s + r.hr,           0) / history.length).toFixed(0) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <div className="text-center py-6 sm:py-10 px-4">
        <div className="inline-flex items-center gap-2 bg-slate-800/60 border border-slate-700 rounded-2xl px-4 py-2 mb-3">
          <span className="text-lg sm:text-2xl">📋</span>
          <span className="text-xs sm:text-sm font-semibold text-slate-300 tracking-widest uppercase">Prediction History</span>
        </div>
        <h1 className="text-3xl sm:text-5xl font-black bg-gradient-to-r from-cyan-400 via-sky-400 to-blue-400 bg-clip-text text-transparent mb-2">History</h1>
        <p className="text-slate-400 text-sm sm:text-lg">Complete record of all stress predictions & trends</p>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 pb-16 space-y-4 sm:space-y-6">

        {history.length === 0 ? (
          <div className="text-center bg-slate-800/60 border border-slate-700 rounded-3xl p-16">
            <div className="text-7xl mb-4">📭</div>
            <h2 className="text-2xl font-bold text-white mb-2">No History Yet</h2>
            <p className="text-slate-400 mb-6">Your prediction records will appear here</p>
            <a href="/prediction" className="inline-block bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:scale-105 transition-transform">Make First Prediction →</a>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryCard icon="🗂️" label="Total Records" value={history.length}  unit=""     color="#8b5cf6" delay={0}/>
              <SummaryCard icon="⚡" label="Avg Stress"    value={avgStress}        unit="/100" color="#ef4444" delay={100}/>
              <SummaryCard icon="🌙" label="Avg Sleep"     value={avgSleep}         unit="hrs"  color="#3b82f6" delay={200}/>
              <SummaryCard icon="❤️" label="Avg Heart Rate" value={avgHR}           unit="bpm"  color="#f97316" delay={300}/>
            </div>

            {/* Trend Chart */}
            <div className="bg-slate-800/60 border border-slate-700/50 rounded-3xl p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">📈</div>
                <div>
                  <h3 className="font-bold text-white">Stress & Sleep Trend</h3>
                  <p className="text-slate-400 text-xs">All-time history overview</p>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="hStress" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/><stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="hSleep" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/><stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b"/>
                  <XAxis dataKey="date" stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 10 }}/>
                  <YAxis stroke="#475569" tick={{ fill: '#94a3b8', fontSize: 10 }}/>
                  <Tooltip content={<CustomTooltip/>}/>
                  <Area type="monotone" dataKey="stress" stroke="#ef4444" strokeWidth={2} fill="url(#hStress)" name="Stress"/>
                  <Area type="monotone" dataKey="sleep"  stroke="#3b82f6" strokeWidth={2} fill="url(#hSleep)"  name="Sleep"/>
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-slate-400 text-sm font-medium">Filter:</span>
              {['All','Low','Mild','Moderate','High','Critical'].map(f => {
                const cfg = RISK_CONFIG[f];
                return (
                  <button key={f} onClick={() => setFilter(f)}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-all duration-200 ${filter === f ? (cfg ? `${cfg.bg} ${cfg.border} ${cfg.text}` : 'bg-slate-600 border-slate-500 text-white') : 'bg-slate-800/60 border-slate-700 text-slate-400 hover:border-slate-500'}`}>
                    {cfg?.emoji} {f}
                  </button>
                );
              })}
              <button onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                className="ml-auto px-4 py-1.5 rounded-full text-sm font-semibold bg-slate-800/60 border border-slate-700 text-slate-400 hover:border-slate-500 transition-all">
                {sortDir === 'desc' ? '↓ Newest' : '↑ Oldest'}
              </button>
            </div>

            {/* Table - hidden on mobile, shown on md+ */}
            <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl sm:rounded-3xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b border-slate-700/50">
                      {['Date','Score','Level','Risk','Sleep','HR','Change','Trend'].map(h => (
                        <th key={h} className="px-3 sm:px-5 py-3 sm:py-4 text-left text-xs font-bold text-slate-400 uppercase tracking-widest">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((record, idx) => {
                      const cfg = RISK_CONFIG[record.risk_category] || RISK_CONFIG.Low;
                      const trendSlice = history.slice(Math.max(0, history.indexOf(record) - 5), history.indexOf(record) + 1)
                        .map(r => ({ v: r.stress_score })).reverse();
                      return (
                        <tr key={record.id}
                          className="border-b border-slate-700/30 hover:bg-slate-700/30 transition-colors duration-150"
                          style={{ animationDelay: `${idx * 30}ms` }}>
                          <td className="px-5 py-4 text-sm text-slate-300 font-medium whitespace-nowrap">{record.date}</td>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black" style={{ background: `${cfg.color}20`, color: cfg.color }}>
                                {Math.round(record.stress_score)}
                              </div>
                              <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${record.stress_score}%`, background: cfg.color }}/>
                              </div>
                            </div>
                          </td>
                          <td className="px-5 py-4 text-sm font-bold text-slate-300">{record.stress_level}/4</td>
                          <td className="px-5 py-4">
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border ${cfg.bg} ${cfg.border} ${cfg.text}`}>
                              {cfg.emoji} {record.risk_category}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-sm text-slate-300">{record.sleep_hours}<span className="text-slate-500 text-xs">h</span></td>
                          <td className="px-5 py-4 text-sm text-slate-300">{record.hr}<span className="text-slate-500 text-xs"> bpm</span></td>
                          <td className="px-5 py-4">
                            {record.change !== null ? (
                              <span className={`text-sm font-bold flex items-center gap-1 ${record.change > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                                {record.change > 0 ? '▲' : '▼'} {Math.abs(record.change).toFixed(1)}
                              </span>
                            ) : <span className="text-slate-600 text-xs">—</span>}
                          </td>
                          <td className="px-5 py-4">
                            <ResponsiveContainer width={80} height={28}>
                              <LineChart data={trendSlice}>
                                <Line type="monotone" dataKey="v" stroke={cfg.color} strokeWidth={2} dot={false}/>
                              </LineChart>
                            </ResponsiveContainer>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 border-t border-slate-700/30 text-xs text-slate-500">
                Showing {filtered.length} of {history.length} records
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
